/**
 * Public Layout Component
 * 
 * Wrapper component for all public pages.
 * Provides consistent layout with navbar and footer.
 */

import React, { useState, useEffect } from "react";
import { PublicNavbar } from "./PublicNavbar";
import { PublicFooter } from "./PublicFooter";
import "./public-layout.css";

interface PublicLayoutProps {
  children: React.ReactNode;
}

export function PublicLayout({ children }: PublicLayoutProps): JSX.Element {
  const [currentPath, setCurrentPath] = useState<string | undefined>(
    typeof window !== "undefined" ? window.location.pathname : undefined
  );

  useEffect(() => {
    // Update currentPath when URL changes (for proper navbar button visibility)
    const updatePath = () => {
      setCurrentPath(window.location.pathname);
    };

    // Update on mount and when pathname changes
    updatePath();
    window.addEventListener("popstate", updatePath);
    
    // Also listen for pushState/replaceState (used by React Router or manual navigation)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args);
      updatePath();
    };
    
    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args);
      updatePath();
    };

    return () => {
      window.removeEventListener("popstate", updatePath);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  return (
    <div className="public-layout">
      <PublicNavbar currentPath={currentPath} />
      <main className="public-layout__main" role="main">
        {children}
      </main>
      <PublicFooter />
    </div>
  );
}

export default PublicLayout;

